import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Image from '../../../components/AppImage';

const StainRemovalGuide = ({ isOpen, onClose }) => {
  const [selectedStain, setSelectedStain] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSurface, setSelectedSurface] = useState('all');

  const stainCategories = [
    { value: 'all', label: 'All Stains' },
    { value: 'food', label: 'Food & Beverages' },
    { value: 'biological', label: 'Biological' },
    { value: 'grease', label: 'Grease & Oil' },
    { value: 'ink', label: 'Ink & Paint' },
    { value: 'other', label: 'Other' }
  ];

  const surfaceTypes = [
    { value: 'all', label: 'All Surfaces' },
    { value: 'carpet', label: 'Carpet' },
    { value: 'upholstery', label: 'Upholstery' },
    { value: 'clothing', label: 'Clothing' },
    { value: 'hard-surfaces', label: 'Hard Surfaces' }
  ];

  const stainGuides = [
    {
      id: 1,
      name: 'Red Wine',
      category: 'food',
      surfaces: ['carpet', 'upholstery', 'clothing'],
      difficulty: 'Medium',
      urgency: 'Act immediately',
      image: 'https://images.pexels.com/photos/544961/pexels-photo-544961.jpeg',
      description: 'Red wine stains can be challenging but are manageable with quick action.',
      materials: [
        'Clean white cloths',
        'Cold water',
        'Salt (for fresh stains)',
        'White wine or club soda',
        'Dish soap',
        'Hydrogen peroxide (for white fabrics)'
      ],
      steps: [
        {
          step: 1,
          title: 'Act Quickly',
          instruction: 'Blot the stain immediately with a clean cloth. Do not rub as this will spread the stain.',
          warning: 'Never rub or scrub the stain'
        },
        {
          step: 2,
          title: 'Apply Salt (Fresh Stains)',
          instruction: 'For fresh stains, cover with salt to absorb the wine. Let sit for 5-10 minutes.',
          tip: 'Salt works best on very fresh stains'
        },
        {
          step: 3,
          title: 'Rinse with Cold Water',
          instruction: 'Rinse the stained area with cold water from the back of the fabric if possible.',
          warning: 'Use cold water only - hot water will set the stain'
        },
        {
          step: 4,
          title: 'Apply Cleaning Solution',
          instruction: 'Mix 1 part dish soap with 3 parts hydrogen peroxide. Apply to stain and let sit for 20 minutes.',
          note: 'Test on hidden area first'
        },
        {
          step: 5,
          title: 'Rinse and Repeat',
          instruction: 'Rinse thoroughly with cold water. Repeat if necessary before the stain dries.',
          tip: 'May require multiple treatments'
        }
      ],
      prevention: [
        'Use stain-resistant treatments on carpets and upholstery',
        'Keep cleaning supplies nearby during wine service',
        'Consider using wine glass charms to prevent spills'
      ],
      alternatives: [
        'White wine can neutralize red wine stains',
        'Club soda can help lift fresh stains',
        'Commercial wine stain removers are available'
      ]
    },
    {
      id: 2,
      name: 'Grease & Oil',
      category: 'grease',
      surfaces: ['clothing', 'carpet', 'hard-surfaces'],
      difficulty: 'Easy',
      urgency: 'Treat as soon as possible',
      image: 'https://images.pexels.com/photos/4239013/pexels-photo-4239013.jpeg',
      description: 'Grease and oil stains require specific techniques to break down the oils.',
      materials: [
        'Cornstarch or baby powder',
        'Dish soap (grease-cutting)',
        'Warm water',
        'Clean cloths',
        'Old toothbrush',
        'Paper towels'
      ],
      steps: [
        {
          step: 1,
          title: 'Remove Excess',
          instruction: 'Scrape off any excess grease or oil with a spoon or knife. Blot with paper towels.',
          warning: 'Do not rub the stain deeper into the fabric'
        },
        {
          step: 2,
          title: 'Absorb Oil',
          instruction: 'Sprinkle cornstarch or baby powder over the stain. Let sit for 15-30 minutes to absorb oil.',
          tip: 'The powder should change color as it absorbs oil'
        },
        {
          step: 3,
          title: 'Remove Powder',
          instruction: 'Brush or vacuum away the powder. You should see the stain has lightened.',
          note: 'Repeat if stain is still heavy'
        },
        {
          step: 4,
          title: 'Apply Dish Soap',
          instruction: 'Apply a small amount of grease-cutting dish soap directly to the stain. Work in gently.',
          tip: 'Dawn or similar degreasing soaps work best'
        },
        {
          step: 5,
          title: 'Rinse and Wash',
          instruction: 'Rinse with warm water. For clothing, wash in the hottest water safe for the fabric.',
          warning: 'Check that stain is gone before drying'
        }
      ],
      prevention: [
        'Use aprons when cooking',
        'Keep napkins handy during meals',
        'Treat stains immediately'
      ],
      alternatives: [
        'WD-40 can help with set-in grease stains',
        'Shampoo can work as a substitute for dish soap',
        'Commercial degreasing products'
      ]
    },
    {
      id: 3,
      name: 'Blood',
      category: 'biological',
      surfaces: ['clothing', 'carpet', 'upholstery'],
      difficulty: 'Medium',
      urgency: 'Treat immediately',
      image: 'https://images.pexels.com/photos/6197119/pexels-photo-6197119.jpeg',
      description: 'Blood stains require cold water and specific techniques to avoid setting the protein.',
      materials: [
        'Cold water',
        'Hydrogen peroxide',
        'Salt',
        'Meat tenderizer (unseasoned)',
        'Clean cloths',
        'Enzyme cleaner'
      ],
      steps: [
        {
          step: 1,
          title: 'Use Cold Water Only',
          instruction: 'Rinse the stain with cold water immediately. Hot water will set the protein.',
          warning: 'Never use hot water on blood stains'
        },
        {
          step: 2,
          title: 'Make Salt Paste',
          instruction: 'Mix salt with cold water to make a paste. Apply to the stain and let sit for 10 minutes.',
          tip: 'Salt helps break down the proteins in blood'
        },
        {
          step: 3,
          title: 'Rinse and Assess',
          instruction: 'Rinse with cold water. If stain persists, move to hydrogen peroxide treatment.',
          note: 'Fresh stains may come out with just salt and water'
        },
        {
          step: 4,
          title: 'Apply Hydrogen Peroxide',
          instruction: 'Pour a small amount of hydrogen peroxide on the stain. It will foam as it works.',
          warning: 'Test on hidden area first - may bleach colors'
        },
        {
          step: 5,
          title: 'Rinse Thoroughly',
          instruction: 'Rinse with cold water until all peroxide is removed. Wash as usual.',
          tip: 'Enzyme cleaners work well for old blood stains'
        }
      ],
      prevention: [
        'Keep a first aid kit handy',
        'Treat cuts immediately to prevent dripping',
        'Use protective coverings when handling raw meat'
      ],
      alternatives: [
        'Meat tenderizer paste works on old stains',
        'Enzyme cleaners are very effective',
        'Lemon juice can help lighten stains'
      ]
    },
    {
      id: 4,
      name: 'Ink (Ballpoint Pen)',
      category: 'ink',
      surfaces: ['clothing', 'upholstery', 'hard-surfaces'],
      difficulty: 'Hard',
      urgency: 'Treat quickly',
      image: 'https://images.pexels.com/photos/159751/book-address-book-learning-learn-159751.jpeg',
      description: 'Ink stains require solvents to break down the dyes and oils in the ink.',
      materials: [
        'Rubbing alcohol (70% or higher)',
        'Hairspray (aerosol)',
        'Cotton balls',
        'Clean cloths',
        'Milk (for fresh stains)',
        'Acetone (nail polish remover)'
      ],
      steps: [
        {
          step: 1,
          title: 'Test First',
          instruction: 'Test your chosen solvent on a hidden area to ensure it won\'t damage the material.',
          warning: 'Some solvents can damage or discolor fabrics'
        },
        {
          step: 2,
          title: 'Apply Rubbing Alcohol',
          instruction: 'Dab rubbing alcohol on a cotton ball and blot the stain from the outside in.',
          tip: 'Work from outside to prevent spreading'
        },
        {
          step: 3,
          title: 'Blot, Don\'t Rub',
          instruction: 'Continue blotting with fresh cotton balls until no more ink transfers.',
          warning: 'Rubbing will spread the stain'
        },
        {
          step: 4,
          title: 'Try Hairspray Method',
          instruction: 'If alcohol doesn\'t work, spray aerosol hairspray on the stain and blot immediately.',
          note: 'Old-fashioned hairspray works better than new formulas'
        },
        {
          step: 5,
          title: 'Wash Thoroughly',
          instruction: 'Rinse the area thoroughly and wash as usual. Check before drying.',
          tip: 'Heat from the dryer will set any remaining stain'
        }
      ],
      prevention: [
        'Use pens with caps',
        'Store pens properly',
        'Check pockets before washing clothes'
      ],
      alternatives: [
        'Milk can work on fresh ink stains',
        'Hand sanitizer contains alcohol and can work',
        'Commercial ink removers are available'
      ]
    },
    {
      id: 5,
      name: 'Coffee',
      category: 'food',
      surfaces: ['clothing', 'carpet', 'upholstery'],
      difficulty: 'Easy',
      urgency: 'Act quickly',
      image: 'https://images.pexels.com/photos/302899/pexels-photo-302899.jpeg',
      description: 'Coffee stains are water-soluble and generally easy to remove when treated promptly.',
      materials: [
        'Cold water',
        'White vinegar',
        'Dish soap',
        'Clean cloths',
        'Baking soda',
        'Club soda'
      ],
      steps: [
        {
          step: 1,
          title: 'Blot Immediately',
          instruction: 'Blot up as much coffee as possible with a clean cloth. Don\'t rub.',
          warning: 'Rubbing will push the stain deeper'
        },
        {
          step: 2,
          title: 'Rinse with Cold Water',
          instruction: 'Rinse the stained area with cold water from the back of the fabric if possible.',
          tip: 'Cold water prevents the stain from setting'
        },
        {
          step: 3,
          title: 'Apply Vinegar Solution',
          instruction: 'Mix 1 part white vinegar with 2 parts cold water. Apply to the stain.',
          note: 'Vinegar helps break down the coffee compounds'
        },
        {
          step: 4,
          title: 'Add Dish Soap',
          instruction: 'Apply a small amount of dish soap and gently work it in with your fingers.',
          tip: 'Let it sit for 5 minutes before rinsing'
        },
        {
          step: 5,
          title: 'Rinse and Wash',
          instruction: 'Rinse thoroughly with cold water and wash as usual.',
          warning: 'Check that stain is gone before drying'
        }
      ],
      prevention: [
        'Use travel mugs with secure lids',
        'Keep napkins handy',
        'Be careful when walking with coffee'
      ],
      alternatives: [
        'Club soda can help lift fresh stains',
        'Baking soda paste works on set stains',
        'Commercial coffee stain removers'
      ]
    },
    {
      id: 6,
      name: 'Mud',
      category: 'other',
      surfaces: ['clothing', 'carpet', 'hard-surfaces'],
      difficulty: 'Easy',
      urgency: 'Let dry first',
      image: 'https://images.pexels.com/photos/1029896/pexels-photo-1029896.jpeg',
      description: 'Mud stains are easier to remove when completely dry. Never treat wet mud.',
      materials: [
        'Vacuum cleaner',
        'Soft brush',
        'Cold water',
        'Laundry detergent',
        'White vinegar',
        'Clean cloths'
      ],
      steps: [
        {
          step: 1,
          title: 'Let Mud Dry Completely',
          instruction: 'Allow the mud to dry completely before attempting removal. This prevents spreading.',
          warning: 'Never treat wet mud - it will spread and make the stain worse'
        },
        {
          step: 2,
          title: 'Remove Dried Mud',
          instruction: 'Brush or vacuum away as much dried mud as possible.',
          tip: 'Use a soft brush to avoid damaging the fabric'
        },
        {
          step: 3,
          title: 'Pre-treat with Detergent',
          instruction: 'Apply liquid laundry detergent directly to the remaining stain. Let sit for 15 minutes.',
          note: 'Work the detergent in gently with your fingers'
        },
        {
          step: 4,
          title: 'Rinse with Cold Water',
          instruction: 'Rinse the area with cold water, working from the outside of the stain inward.',
          tip: 'Use a spray bottle for better control'
        },
        {
          step: 5,
          title: 'Wash as Usual',
          instruction: 'Wash the item in the hottest water safe for the fabric.',
          warning: 'Check that stain is gone before drying'
        }
      ],
      prevention: [
        'Use door mats',
        'Remove shoes when entering',
        'Treat clothing with stain repellent'
      ],
      alternatives: [
        'White vinegar can help with stubborn mud stains',
        'Enzyme cleaners work well on organic matter',
        'Commercial pre-treatment sprays'
      ]
    }
  ];

  const filteredStains = stainGuides?.filter(stain => {
    const matchesSearch = stain?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         stain?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || stain?.category === selectedCategory;
    const matchesSurface = selectedSurface === 'all' || stain?.surfaces?.includes(selectedSurface);
    
    return matchesSearch && matchesCategory && matchesSurface;
  });

  const handleStainSelect = (stain) => {
    setSelectedStain(stain);
  };

  const closeStainDetail = () => {
    setSelectedStain(null);
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'easy': return 'text-success bg-success/10';
      case 'medium': return 'text-warning bg-warning/10';
      case 'hard': return 'text-error bg-error/10';
      default: return 'text-text-secondary bg-muted';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-elevation-5 w-full max-w-6xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-error/10 rounded-lg flex items-center justify-center">
              <Icon name="Droplets" size={24} color="var(--color-error)" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-text-primary">
                Stain Removal Guide
              </h2>
              <p className="text-sm text-text-secondary">
                Step-by-step solutions for common household stains
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Close stain guide"
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {selectedStain ? (
          /* Stain Detail View */
          (<div className="flex flex-col h-full max-h-[calc(90vh-80px)]">
            <div className="p-6 flex-1 overflow-y-auto">
              <div className="max-w-4xl mx-auto">
                {/* Stain Header */}
                <div className="flex items-start space-x-4 mb-6">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={closeStainDetail}
                    aria-label="Back to guide"
                  >
                    <Icon name="ArrowLeft" size={20} />
                  </Button>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-text-primary mb-2">
                      {selectedStain?.name} Stain Removal
                    </h3>
                    <p className="text-text-secondary mb-4">{selectedStain?.description}</p>
                    <div className="flex items-center space-x-4 text-sm">
                      <span className={`px-3 py-1 rounded-full font-medium ${getDifficultyColor(selectedStain?.difficulty)}`}>
                        {selectedStain?.difficulty}
                      </span>
                      <span className="flex items-center space-x-1 text-error">
                        <Icon name="Clock" size={14} />
                        <span>{selectedStain?.urgency}</span>
                      </span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Stain Image */}
                  <div className="lg:col-span-1">
                    <div className="w-full h-64 rounded-lg overflow-hidden bg-muted mb-4">
                      <Image
                        src={selectedStain?.image}
                        alt={selectedStain?.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    
                    {/* Materials Needed */}
                    <div className="bg-surface rounded-lg p-4">
                      <h4 className="font-semibold text-text-primary mb-3 flex items-center space-x-2">
                        <Icon name="Package" size={18} />
                        <span>Materials Needed</span>
                      </h4>
                      <ul className="space-y-1">
                        {selectedStain?.materials?.map((material, index) => (
                          <li key={index} className="text-sm text-text-secondary flex items-center space-x-2">
                            <Icon name="Check" size={12} color="var(--color-success)" />
                            <span>{material}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Step-by-Step Instructions */}
                  <div className="lg:col-span-2 space-y-6">
                    <div>
                      <h4 className="font-semibold text-text-primary mb-4 flex items-center space-x-2">
                        <Icon name="List" size={18} />
                        <span>Step-by-Step Instructions</span>
                      </h4>
                      <div className="space-y-4">
                        {selectedStain?.steps?.map((step) => (
                          <div key={step?.step} className="bg-surface rounded-lg p-4">
                            <div className="flex items-start space-x-3">
                              <span className="flex-shrink-0 w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-medium">
                                {step?.step}
                              </span>
                              <div className="flex-1">
                                <h5 className="font-medium text-text-primary mb-2">{step?.title}</h5>
                                <p className="text-sm text-text-secondary mb-2">{step?.instruction}</p>
                                {step?.warning && (
                                  <div className="flex items-start space-x-2 p-2 bg-error/10 rounded text-error text-xs">
                                    <Icon name="AlertTriangle" size={12} className="mt-0.5 flex-shrink-0" />
                                    <span>{step?.warning}</span>
                                  </div>
                                )}
                                {step?.tip && (
                                  <div className="flex items-start space-x-2 p-2 bg-primary/10 rounded text-primary text-xs">
                                    <Icon name="Lightbulb" size={12} className="mt-0.5 flex-shrink-0" />
                                    <span>{step?.tip}</span>
                                  </div>
                                )}
                                {step?.note && (
                                  <div className="flex items-start space-x-2 p-2 bg-muted rounded text-text-secondary text-xs">
                                    <Icon name="Info" size={12} className="mt-0.5 flex-shrink-0" />
                                    <span>{step?.note}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Prevention Tips */}
                    <div>
                      <h4 className="font-semibold text-text-primary mb-3 flex items-center space-x-2">
                        <Icon name="Shield" size={18} />
                        <span>Prevention Tips</span>
                      </h4>
                      <ul className="space-y-2">
                        {selectedStain?.prevention?.map((tip, index) => (
                          <li key={index} className="text-sm text-text-secondary flex items-start space-x-2">
                            <Icon name="CheckCircle" size={14} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                            <span>{tip}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Alternative Methods */}
                    <div>
                      <h4 className="font-semibold text-text-primary mb-3 flex items-center space-x-2">
                        <Icon name="Repeat" size={18} />
                        <span>Alternative Methods</span>
                      </h4>
                      <ul className="space-y-2">
                        {selectedStain?.alternatives?.map((alternative, index) => (
                          <li key={index} className="text-sm text-text-secondary flex items-start space-x-2">
                            <Icon name="ArrowRight" size={14} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" />
                            <span>{alternative}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>)
        ) : (
          /* Stain Grid View */
          (<div className="flex flex-col h-full max-h-[calc(90vh-80px)]">
            {/* Search and Filter */}
            <div className="p-6 border-b border-border">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <Input
                    type="search"
                    placeholder="Search for stain type..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e?.target?.value)}
                    className="w-full"
                  />
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Select
                    options={stainCategories}
                    value={selectedCategory}
                    onChange={setSelectedCategory}
                    placeholder="Category"
                    className="min-w-40"
                  />
                  <Select
                    options={surfaceTypes}
                    value={selectedSurface}
                    onChange={setSelectedSurface}
                    placeholder="Surface"
                    className="min-w-40"
                  />
                </div>
              </div>
            </div>
            {/* Stains Grid */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredStains?.map((stain) => (
                  <div
                    key={stain?.id}
                    className="bg-surface rounded-lg border border-border overflow-hidden hover:shadow-elevation-2 transition-all duration-200 cursor-pointer"
                    onClick={() => handleStainSelect(stain)}
                  >
                    <div className="h-48 overflow-hidden bg-muted">
                      <Image
                        src={stain?.image}
                        alt={stain?.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-text-primary">{stain?.name}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(stain?.difficulty)}`}>
                          {stain?.difficulty}
                        </span>
                      </div>
                      <p className="text-sm text-text-secondary mb-3 line-clamp-2">
                        {stain?.description}
                      </p>
                      <div className="flex items-center justify-between text-xs text-text-secondary">
                        <span className="flex items-center space-x-1">
                          <Icon name="Clock" size={12} />
                          <span>{stain?.urgency}</span>
                        </span>
                        <div className="flex space-x-1">
                          {stain?.surfaces?.slice(0, 2)?.map((surface) => (
                            <span key={surface} className="px-2 py-1 bg-muted rounded text-xs">
                              {surface}
                            </span>
                          ))}
                          {stain?.surfaces?.length > 2 && (
                            <span className="px-2 py-1 bg-muted rounded text-xs">
                              +{stain?.surfaces?.length - 2}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {filteredStains?.length === 0 && (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="Search" size={32} color="var(--color-text-secondary)" />
                  </div>
                  <h3 className="text-lg font-medium text-text-primary mb-2">
                    No stains found
                  </h3>
                  <p className="text-text-secondary">
                    Try adjusting your search or filter criteria
                  </p>
                </div>
              )}
            </div>
          </div>)
        )}
      </div>
    </div>
  );
};

export default StainRemovalGuide;